import { useNavigate, Link } from 'react-router-dom';
import StatTile from './components/StatTile';
import BarListPanel from './components/BarListPanel';
import PointsChart from './components/PointsChart';
import ActivityList from './components/ActivityList';
import { overviewKpis, pointsActivity, topRewards, integrationStatus } from '@/mocks/dashboard';
import { rewards, redemptions } from '@/mocks/rewards';

const kpi = (id: string) => overviewKpis.find((item) => item.id === id)?.value ?? '0';
const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);
const calls = (value: string) => Number(value.replace(/[^0-9.]/g, ''));

const STATUS_ROWS = [
  { status: 'pending', label: 'Pending', color: 'bg-[#c9812f]' },
  { status: 'approved', label: 'Approved', color: 'bg-[#3b64e0]' },
  { status: 'fulfilled', label: 'Fulfilled', color: 'bg-[#46a35a]' },
  { status: 'rejected', label: 'Rejected', color: 'bg-[#cf3a3a]' },
];

export default function DashboardOverview() {
  const navigate = useNavigate();
  const earned = sum(pointsActivity.map((day) => day.earned));
  const redeemed = sum(pointsActivity.map((day) => day.redeemed));
  const disabledRewards = rewards.filter((reward) => reward.status === 'disabled').length;
  const statusRows = STATUS_ROWS.map(({ status, label, color }) => ({
    label,
    value: redemptions.filter((item) => item.status === status).length,
    color,
  }));

  const stats = [
    { label: 'Total members', value: kpi('members'), icon: 'ri-group-line', tone: 'bg-[#e8f2fb] text-[#2f7fc4]' },
    { label: 'Points issued', value: kpi('points'), icon: 'ri-coins-line', tone: 'bg-[#f1ebfd] text-[#7c4fe0]' },
    { label: 'Points earned', value: `${earned.toLocaleString()}k`, icon: 'ri-line-chart-line', tone: 'bg-[#e8f6ec] text-[#2f8f4e]' },
    { label: 'Points redeemed', value: `${redeemed.toLocaleString()}k`, icon: 'ri-gift-2-line', tone: 'bg-[#fdeee8] text-[#e0703a]' },
    { label: 'Pending redemptions', value: kpi('redemptions'), icon: 'ri-time-line', tone: 'bg-[#fdf3e3] text-[#c9812f]' },
    { label: 'Active integrations', value: kpi('integrations'), icon: 'ri-plug-line', tone: 'bg-[#e6f4f1] text-[#2f7a68]' },
    { label: 'Active rewards', value: String(rewards.filter((reward) => reward.status === 'active').length), icon: 'ri-price-tag-3-line', tone: 'bg-[#fdeaf3] text-[#d9408f]' },
    { label: 'Total redeemed', value: sum(topRewards.map((reward) => reward.redeemed)).toLocaleString(), icon: 'ri-checkbox-circle-line', tone: 'bg-[#e8edfc] text-[#3b64e0]' },
    { label: 'API calls this month', value: sum(integrationStatus.map((platform) => calls(platform.calls))).toLocaleString(), icon: 'ri-bar-chart-2-line', tone: 'bg-[#eef1f5] text-[#526176]' },
    { label: 'Redeem rate', value: `${Math.round((redeemed / earned) * 100)}%`, icon: 'ri-percent-line', tone: 'bg-[#fbe9e9] text-[#cf3a3a]' },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-foreground-500">Overview</p>
          <h1 className="mt-1 font-heading font-bold text-[28px] leading-9 text-foreground-950">Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => window.location.reload()} className="px-4 py-2.5 rounded-xl bg-white border border-background-200 text-sm font-semibold text-primary-600 hover:bg-background-100 cursor-pointer">
            Refresh
          </button>
          <button type="button" onClick={() => navigate('/dashboard/rewards?new=1')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 cursor-pointer">
            <i className="ri-add-line" aria-hidden="true" /> New reward
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {stats.map((stat) => <StatTile key={stat.label} {...stat} />)}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[3fr_2fr] gap-5">
        <PointsChart />
        <BarListPanel title="Redemptions by reward" rows={topRewards.map((reward) => ({ label: reward.name, value: reward.redeemed }))} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <BarListPanel title="Redemption status" rows={statusRows} />
        <BarListPanel title="API traffic by platform" rows={integrationStatus.map((platform) => ({ label: platform.name, value: calls(platform.calls), color: 'bg-[#46a35a]' }))} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[2fr_3fr] gap-5">
        <div className="bg-white border border-background-200 rounded-2xl p-5 md:p-6">
          <h3 className="font-heading font-semibold text-foreground-950 mb-4">Needs attention</h3>
          <ul className="flex flex-col gap-3 text-sm">
            <li className="flex items-center gap-3">
              <span className="px-2.5 py-0.5 rounded-full bg-[#eef0f4] text-xs font-medium text-foreground-700">Redemptions</span>
              <Link to="/dashboard/redemptions" className="font-medium text-foreground-950 hover:underline">Pending redemptions: {kpi('redemptions')}</Link>
            </li>
            <li className="flex items-center gap-3">
              <span className="px-2.5 py-0.5 rounded-full bg-[#eef0f4] text-xs font-medium text-foreground-700">Rewards</span>
              <Link to="/dashboard/rewards" className="font-medium text-foreground-950 hover:underline">Disabled rewards: {disabledRewards}</Link>
            </li>
          </ul>
        </div>
        <ActivityList />
      </div>
    </div>
  );
}
